import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Search, ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';
import api from '../utils/api';

export default function UsersScreen() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [userLimits, setUserLimits] = useState(null);
  const [kycFilter, setKycFilter] = useState('all'); // all | kyc | nokyc
  const [ordersFilter, setOrdersFilter] = useState('all'); // all | has | none
  const [sortBy, setSortBy] = useState('date'); // date | orders | volume
  const [sortDir, setSortDir] = useState('desc'); // asc | desc

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getUsers();
      setUsers(data.users || data.data || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const toggleUserDetails = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setUserOrders([]);
      setUserInfo(null);
      return;
    }
    setExpandedUser(userId);
    setDetailsLoading(true);
    setUserOrders([]);
    setUserInfo(null);
    setUserLimits(null);

    try {
      const [data, limitsData] = await Promise.all([
        api.getUserAnalytics(userId),
        api.getUserLimits(userId).catch(() => null),
      ]);

      // Extract orders from events
      const orders = (data.events || data.orders || [])
        .filter(e => e.eventName === 'order_created' || e.event_name === 'order_created')
        .map(e => {
          let eventData = e.eventData || e.event_data;
          if (typeof eventData === 'string') {
            try { eventData = JSON.parse(eventData); } catch { eventData = {}; }
          }
          return {
            orderId: eventData?.order_id,
            amountCny: eventData?.amount_cny,
            paymentMethod: eventData?.payment_method,
            date: e.createdAt || e.created_at,
          };
        })
        .filter(o => o.orderId);

      setUserOrders(orders);
      setUserLimits(limitsData);

      // Store raw user info
      const info = { ...data };
      delete info.events;
      delete info.orders;
      setUserInfo(info);
    } catch {
      setUserOrders([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const getUserDate = (user) => {
    const raw = user.firstSeen || user.createdAt || user.created_at || user.registeredAt;
    if (!raw) return 0;
    return new Date(raw).getTime() || 0;
  };

  const getUserOrdersCount = (user) => {
    return Number(user.ordersCount ?? user.orders_count ?? 0);
  };

  const getUserVolume = (user) => {
    return Number(user.ordersTotal ?? user.completedTotal ?? user.totalVolume ?? 0);
  };

  const hasKyc = (user) => !!(user.kyc?.isVerified || user.isVerified || user.is_verified);

  const filteredUsers = useMemo(() => {
    let result = users.filter(user => {
      // Search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match =
          String(user.userId || user.user_id || user.id || '').includes(q) ||
          String(user.username || '').toLowerCase().includes(q) ||
          String(user.firstName || user.first_name || '').toLowerCase().includes(q) ||
          String(user.phone || user.kyc?.phone || '').includes(q);
        if (!match) return false;
      }
      // KYC filter
      if (kycFilter === 'kyc' && !hasKyc(user)) return false;
      if (kycFilter === 'nokyc' && hasKyc(user)) return false;
      // Orders filter
      const cnt = getUserOrdersCount(user);
      if (ordersFilter === 'has' && cnt === 0) return false;
      if (ordersFilter === 'none' && cnt > 0) return false;
      return true;
    });

    // Sort
    result.sort((a, b) => {
      let va, vb;
      if (sortBy === 'date') { va = getUserDate(a); vb = getUserDate(b); }
      else if (sortBy === 'orders') { va = getUserOrdersCount(a); vb = getUserOrdersCount(b); }
      else if (sortBy === 'volume') { va = getUserVolume(a); vb = getUserVolume(b); }
      else { va = 0; vb = 0; }
      return sortDir === 'desc' ? vb - va : va - vb;
    });

    return result;
  }, [users, searchQuery, kycFilter, ordersFilter, sortBy, sortDir]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">
          Пользователи
          {!loading && <span className="text-sm text-gray-400 font-normal ml-2">({filteredUsers.length}{filteredUsers.length !== users.length ? ` из ${users.length}` : ''})</span>}
        </h2>
        <button
          onClick={loadUsers}
          disabled={loading}
          className="p-2 bg-gray-800 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Поиск по ID, username, имени, телефону..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
        />
      </div>

      {/* Filters & Sort */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* KYC filter */}
        <div className="flex bg-gray-800 rounded-lg border border-gray-700/50 overflow-hidden">
          {[
            { value: 'all', label: 'Все' },
            { value: 'kyc', label: 'KYC' },
            { value: 'nokyc', label: 'Без KYC' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setKycFilter(f.value)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                kycFilter === f.value
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Orders filter */}
        <div className="flex bg-gray-800 rounded-lg border border-gray-700/50 overflow-hidden">
          {[
            { value: 'all', label: 'Все' },
            { value: 'has', label: 'С ордерами' },
            { value: 'none', label: 'Без ордеров' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setOrdersFilter(f.value)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                ordersFilter === f.value
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Sort buttons */}
        <div className="flex gap-1.5 ml-auto">
          {[
            { value: 'date', label: 'Дата' },
            { value: 'orders', label: 'Ордера' },
            { value: 'volume', label: 'Объём' },
          ].map(s => (
            <button
              key={s.value}
              onClick={() => toggleSort(s.value)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                sortBy === s.value
                  ? 'bg-purple-600/20 text-purple-400 border-purple-500/40'
                  : 'bg-gray-800 text-gray-400 hover:text-white border-gray-700/50'
              }`}
            >
              {s.label}
              {sortBy === s.value && (
                <ArrowUpDown size={10} className={sortDir === 'asc' ? 'rotate-180' : ''} />
              )}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-gray-800 rounded-xl border border-gray-700/50 p-4">
              <div className="h-5 bg-gray-700 rounded animate-pulse w-1/2" />
            </div>
          ))
        ) : filteredUsers.length === 0 ? (
          <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-8 text-center text-gray-500">
            Пользователи не найдены
          </div>
        ) : (
          filteredUsers.map((user, i) => {
            const uid = user.userId || user.user_id || user.id;
            const isExpanded = expandedUser === uid;
            return (
              <div key={uid || i} className="bg-gray-800 rounded-xl border border-gray-700/50 overflow-hidden">
                <button
                  onClick={() => toggleUserDetails(uid)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-700/30 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
                      {(user.firstName || user.first_name || user.username || 'U')[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate flex items-center gap-2">
                        <span>{user.firstName || user.first_name || ''} {user.lastName || user.last_name || ''}</span>
                        {user.username && <span className="text-gray-400">@{user.username}</span>}
                        {getUserVolume(user) > 0 && (
                          <span className="text-green-400 text-xs font-normal">{Math.round(getUserVolume(user)).toLocaleString('ru-RU')} ¥</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {uid}
                        {(user.phone || user.kyc?.phone) && ` · ${user.phone || user.kyc.phone}`}
                        {getUserDate(user) > 0 && ` · ${formatDate(user.firstSeen || user.createdAt || user.created_at)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {hasKyc(user) ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">KYC</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-500/20 text-gray-400">Без KYC</span>
                    )}
                    <span className="text-xs text-gray-400">
                      {getUserOrdersCount(user)} орд.
                    </span>
                    {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-700 p-4 bg-gray-800/50">
                    {detailsLoading ? (
                      <div className="h-16 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : (
                      <>
                        {/* User summary */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                          <div>
                            <p className="text-xs text-gray-500">Сессий</p>
                            <p className="text-white">{user.sessionsCount ?? user.sessions_count ?? '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Рефералы</p>
                            <p className="text-white">{user.referralsCount ?? user.referrals ?? '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">KYC статус</p>
                            <p className="text-white text-xs">{user.kyc?.passportState || '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Регистрация</p>
                            <p className="text-white text-xs">{formatDate(user.firstSeen || user.createdAt || user.created_at)}</p>
                          </div>
                        </div>

                        {/* Orders */}
                        <p className="text-xs text-gray-400 font-medium mb-2">
                          Ордера ({userOrders.length})
                        </p>
                        {userOrders.length === 0 ? (
                          <p className="text-xs text-gray-500">Нет ордеров</p>
                        ) : (
                          <div className="space-y-1.5 max-h-60 overflow-y-auto">
                            {userOrders.map((order, j) => (
                              <div
                                key={order.orderId || j}
                                className="flex items-center justify-between bg-gray-700/30 rounded-lg px-3 py-2"
                              >
                                <div>
                                  <span className="text-xs text-white font-mono">#{order.orderId}</span>
                                  <span className="text-xs text-gray-400 ml-2">{order.paymentMethod || '—'}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-xs text-white font-medium">{Math.round(order.amountCny)} ¥</span>
                                  <span className="text-xs text-gray-500 ml-2">{formatDate(order.date)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Limits */}
                        {userLimits && (userLimits.sbp || userLimits.vtb) && (
                          <div className="mt-4">
                            <p className="text-xs text-gray-400 font-medium mb-2">Лимиты</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {['sbp', 'vtb'].map(method => {
                                const lim = userLimits[method];
                                if (!lim) return null;
                                return (
                                  <div key={method} className="bg-gray-700/30 rounded-lg p-3">
                                    <p className="text-xs text-white font-medium mb-2">{method.toUpperCase()}</p>
                                    <p className="text-xs text-gray-500 mb-1">На транзакцию: <span className="text-white">{(lim.perTransaction ?? 0).toLocaleString('ru-RU')} ₽</span></p>
                                    {lim.daily && (
                                      <div className="mb-1.5">
                                        <div className="flex justify-between text-xs mb-0.5">
                                          <span className="text-gray-500">Дневной</span>
                                          <span className="text-white">{(lim.daily.used ?? 0).toLocaleString('ru-RU')} / {(lim.daily.limit ?? 0).toLocaleString('ru-RU')} ₽</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-gray-600 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full rounded-full ${(lim.daily.percent ?? 0) > 80 ? 'bg-red-500' : (lim.daily.percent ?? 0) > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                                            style={{ width: `${Math.min(lim.daily.percent ?? 0, 100)}%` }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                    {lim.monthly && (
                                      <div>
                                        <div className="flex justify-between text-xs mb-0.5">
                                          <span className="text-gray-500">Месячный</span>
                                          <span className="text-white">{(lim.monthly.used ?? 0).toLocaleString('ru-RU')} / {(lim.monthly.limit ?? 0).toLocaleString('ru-RU')} ₽</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-gray-600 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full rounded-full ${(lim.monthly.percent ?? 0) > 80 ? 'bg-red-500' : (lim.monthly.percent ?? 0) > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                                            style={{ width: `${Math.min(lim.monthly.percent ?? 0, 100)}%` }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
